import React, { useEffect, useState } from 'react';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { enqueueSnackbar } from 'notistack';
import {
  ExportHubAPI,
  ExportProduct,
  ExportJurisdiction,
} from 'api/ExportHubAPI';
import { JurisdictionPill, JURISDICTION_OPTIONS } from './productPresets';
import StyledSingleSelect from './StyledSingleSelect';
import {
  ALL_JUR_FIELD_KEYS,
  JurisdictionDetailsFields,
  JurDetails,
  jurisdictionDetailRows,
  toJurDetails,
  JUR_NOTE,
  hasJurisdictionFields,
} from './jurisdictionFields';

interface Props {
  product: ExportProduct;
  groupId: string | undefined;
  onChanged: () => void;
  // The form needs the full width, so the parent collapses the two-column
  // layout while this panel is in edit mode.
  onEditingChange?: (_editing: boolean) => void;
}

/**
 * The recorded export-control regime and its regime-specific detail — the left
 * rail of the Export Control Regime section. Renders bare (no panel chrome):
 * the parent wraps this and the workflow findings in one panel so they read as
 * a single section.
 *
 * Read view shows the regime pill + fields; edit mode is the guided cleanup
 * path for products flagged "Needs Jurisdiction" (a controlled regime with no
 * detail captured yet). Check findings are no longer restated here — they sit
 * beside this rail as the workflow result cards, which flag their own
 * disagreements with the recorded regime.
 */
const JurisdictionDetailsPanel: React.FC<Props> = ({
  product,
  groupId,
  onChanged,
  onEditingChange,
}) => {
  const [editing, setEditing] = useState(false);
  const [jurisdiction, setJurisdiction] = useState<ExportJurisdiction>(
    product.jurisdiction,
  );
  const [details, setDetails] = useState<JurDetails>(
    toJurDetails(product.jurisdiction_details),
  );
  const [saving, setSaving] = useState(false);

  const savedDetails = toJurDetails(product.jurisdiction_details);
  const rows = jurisdictionDetailRows(product.jurisdiction, savedDetails);
  const needsDetail =
    hasJurisdictionFields(product.jurisdiction) && rows.length === 0;

  useEffect(() => {
    if (onEditingChange) onEditingChange(editing);
  }, [editing, onEditingChange]);

  const startEdit = () => {
    setJurisdiction(product.jurisdiction);
    setDetails(toJurDetails(product.jurisdiction_details));
    setEditing(true);
  };

  const save = async () => {
    if (!groupId) return;
    setSaving(true);
    // Patch the keys the regime forms own rather than replacing the blob with
    // this component's copy of it: `details` was seeded when edit mode opened,
    // so a replace would silently undo anything written to the product since —
    // a workflow ingest filling in usml_category, or another user's save.
    // Every owned key is sent, values as themselves and blanks as null, so
    // switching regime still clears the previous regime's detail. Keys no form
    // owns are left out of the patch entirely and survive untouched.
    const detailsPatch: Record<string, string | null> = {};
    ALL_JUR_FIELD_KEYS.forEach((k) => {
      detailsPatch[k] = (details[k] || '').trim() || null;
    });
    try {
      await ExportHubAPI.updateProduct(product.id, groupId, {
        jurisdiction: jurisdiction,
        jurisdiction_details_patch: detailsPatch,
      });
      enqueueSnackbar('Jurisdiction details saved.', { variant: 'success' });
      setEditing(false);
      onChanged();
    } catch (err) {
      console.error('Failed to save jurisdiction:', err);
      enqueueSnackbar('Failed to save jurisdiction details.', {
        variant: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div className='eh-regime-subhead'>
        <span>Recorded regime</span>
        {!editing && (
          <button className='eh-mini-btn' type='button' onClick={startEdit}>
            <EditOutlinedIcon style={{ fontSize: 15 }} /> Edit
          </button>
        )}
      </div>

      {!editing ? (
        <>
          <div className='eh-detail-row'>
            <span className='eh-detail-label'>Regime</span>
            <span className='eh-detail-value'>
              <JurisdictionPill value={product.jurisdiction} />
            </span>
          </div>

          {needsDetail && (
            <div className='eh-needs-jur'>
              <WarningAmberIcon style={{ fontSize: 14 }} />
              Jurisdiction detail required for {product.jurisdiction}. Add it to
              complete this product&rsquo;s compliance record.
            </div>
          )}

          {rows.map((r) => (
            <div className='eh-detail-row' key={r.label}>
              <span className='eh-detail-label'>{r.label}</span>
              <span className='eh-detail-value'>{r.value}</span>
            </div>
          ))}

          {!needsDetail && rows.length === 0 && (
            <div className='eh-detail-note'>
              {JUR_NOTE[product.jurisdiction] ||
                'No additional jurisdiction detail is required.'}
            </div>
          )}
        </>
      ) : (
        <>
          <div className='eh-field'>
            <span className='eh-field-label'>Regime</span>
            <StyledSingleSelect
              options={JURISDICTION_OPTIONS.map((j) => ({ value: j, label: j }))}
              value={jurisdiction}
              onChange={(v) => {
                setJurisdiction(v as ExportJurisdiction);
                setDetails({});
              }}
              inline
            />
          </div>

          <JurisdictionDetailsFields
            jurisdiction={jurisdiction}
            values={details}
            onChange={setDetails}
          />

          <div className='eh-panel-actions'>
            <button
              className='btn-outline'
              type='button'
              onClick={() => setEditing(false)}
              disabled={saving}
            >
              Cancel
            </button>
            <button
              className='btn-primary'
              type='button'
              onClick={save}
              disabled={saving}
              style={{ opacity: saving ? 0.6 : 1 }}
            >
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </>
      )}

    </>
  );
};

export default JurisdictionDetailsPanel;
